import { useMemo, useState, type ReactNode } from 'react';

/**
 * Column sorting for `table.grid` (D-047). A screen declares its sortable columns once (as a
 * module constant, so the memo holds), gets its rows back in order, and renders one
 * <SortHeader> per column. Clicking a heading sorts by it; clicking it again reverses.
 * Text sorts case-insensitively with natural numbers, numbers numerically, booleans as 0/1.
 * Empty values sink to the bottom whichever way the column points, and ties keep their
 * original order.
 */

export type SortDir = 'asc' | 'desc';
export type SortValue = string | number | boolean | null | undefined;

export interface SortColumn<T> {
  key: string;
  /** The value the column sorts by. */
  get: (row: T) => SortValue;
  /** Direction of the first click. Counts and yes/no columns read best with the big ones first. */
  firstDir?: SortDir;
}

export interface SortState {
  key: string;
  dir: SortDir;
}

const isEmpty = (v: SortValue): boolean => v === null || v === undefined || v === '';

export function compareValues(a: SortValue, b: SortValue): number {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Number(a) - Number(b);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

export function sortRows<T>(rows: readonly T[], column: SortColumn<T> | undefined, dir: SortDir): T[] {
  if (!column) return [...rows];
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((row, i) => ({ row, i, v: column.get(row) }))
    .sort((x, y) => {
      const ex = isEmpty(x.v);
      const ey = isEmpty(y.v);
      if (ex || ey) return ex === ey ? x.i - y.i : ex ? 1 : -1;
      return sign * compareValues(x.v, y.v) || x.i - y.i;
    })
    .map((x) => x.row);
}

export function useSortedRows<T>(rows: readonly T[] | null | undefined, columns: ReadonlyArray<SortColumn<T>>, initial: SortState) {
  const [sort, setSort] = useState<SortState>(initial);
  const sorted = useMemo(
    () =>
      sortRows(
        rows ?? [],
        columns.find((c) => c.key === sort.key),
        sort.dir,
      ),
    [rows, columns, sort],
  );
  const toggle = (key: string) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: columns.find((c) => c.key === key)?.firstDir ?? 'asc' }));
  return { sorted, sort, toggle };
}

export function SortHeader({
  label,
  sortKey,
  sort,
  onToggle,
  title,
  align,
}: {
  label: ReactNode;
  sortKey: string;
  sort: SortState;
  onToggle: (key: string) => void;
  title?: string;
  align?: 'left' | 'center' | 'right';
}) {
  const active = sort.key === sortKey;
  return (
    <th aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} style={align ? { textAlign: align } : undefined}>
      <button type="button" className={`sort${active ? ' sort--active' : ''}`} title={title} onClick={() => onToggle(sortKey)}>
        {label}
        <span className="sort__arrow" aria-hidden="true">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}
